import React, { useEffect, useMemo, useState } from "react";
import "slick-carousel/slick/slick.css";
import "slick-carousel/slick/slick-theme.css";
import { Sidebar } from "./Sidebar";
import { Header } from "./Header";
import { Footer } from "./Footer";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { base_url } from "../context/Authcontext";
import "../../src/assets/AdminCss/card.css";
import TopBar from "./components/TopNavbar";
import MarqueeBottom from "./components/MarqueeBottom";
import { useTheme } from "../context/ThemeContext";


const formatDate = (dateString) => {
  if (!dateString) return null;

  const dateObj = new Date(dateString);
  const year = dateObj.getFullYear();
  const month = String(dateObj.getMonth() + 1).padStart(2, "0");
  const day = String(dateObj.getDate()).padStart(2, "0");

  return `${year}-${month}-${day}`; // Only date (YYYY-MM-DD)
};



const compareWithToday = (openDate) => {
  const now = new Date();

  // Get today's date
  const today = formatDate(now);

  // Get tomorrow's date by adding 1 day to the current date
  const tomorrow = formatDate(new Date(now.getTime() + 24 * 60 * 60 * 1000)); // 24 hours in milliseconds

  // Convert openDate string to Date object
  const eventDateObj = new Date(openDate);

  const eventDateFormatted = formatDate(eventDateObj);

  return eventDateFormatted === today || eventDateFormatted === tomorrow;
}



const getMinutesSinceMidnight = (date) => {
  return date.getUTCHours() * 60 + date.getUTCMinutes(); // Ensures UTC time handling
};

/**
 * Normalize the wholeMarket response into:
 * {
 *   [marketId]: [
 *     {
 *       ex: {
 *         availableToBack: [...],
 *         availableToLay: [...]
 *       }
 *     }
 *   ]
 * }
 *
 * Supports both:
 * 1) object keyed by marketId
 * 2) array of market objects containing marketId
 * 3) axios responses where data is already an object
 * 4) JSON-string responses
 */
const normalizeOddsResponse = (responseData) => {
  let payload = responseData;

  if (typeof payload === "string") {
    try {
      payload = JSON.parse(payload);
    } catch (e) {
      console.error("wholeMarket returned invalid JSON:", e);
      return {};
    }
  }

  // Some APIs wrap the actual result inside data/data.
  let raw = payload?.data?.data ?? payload?.data ?? payload;

  if (typeof raw === "string") {
    try {
      raw = JSON.parse(raw);
    } catch (e) {
      console.error("Invalid nested wholeMarket JSON:", e);
      return {};
    }
  }

  const normalized = {};

  if (Array.isArray(raw)) {
    raw.forEach((market) => {
      const marketId = market?.marketId ?? market?.market_id ?? market?.id;
      if (!marketId) return;

      // If this API already returns a runner array, preserve it.
      const value = Array.isArray(market) ? market : [market];
      normalized[String(marketId)] = value;
    });
  } else if (raw && typeof raw === "object") {
    Object.entries(raw).forEach(([marketId, value]) => {
      normalized[String(marketId)] = Array.isArray(value)
        ? value
        : value
          ? [value]
          : [];
    });
  }

  return normalized;
};

const getMarketData = (avail, marketId) => {
  if (!avail || !marketId) return [];

  const value = avail[String(marketId)] ?? avail[marketId];

  if (Array.isArray(value)) return value;
  if (value && typeof value === "object") return [value];

  return [];
};

const hasOdds = (marketData) =>
  Array.isArray(marketData) &&
  marketData.some(
    (market) =>
      (market?.ex?.availableToBack?.length ?? 0) > 0 ||
      (market?.ex?.availableToLay?.length ?? 0) > 0
  );

const buildOdds = (marketData) => {
  const backDivs = [];
  const layDivs = [];

  (marketData || []).forEach((market) => {
    const back = market?.ex?.availableToBack;
    const lay = market?.ex?.availableToLay;

    if (Array.isArray(back) && back.length > 0) {
      backDivs.push({
        price: back[0]?.price ?? null,
        type: "back",
      });
    }

    if (Array.isArray(lay) && lay.length > 0) {
      layDivs.push({
        price: lay[0]?.price ?? null,
        type: "lay",
      });
    }
  });

  while (backDivs.length < 2) {
    backDivs.push({ price: null, type: "back" });
  }

  while (layDivs.length < 2) {
    layDivs.push({ price: null, type: "lay" });
  }

  return [
    backDivs[0],
    layDivs[0],
    { price: null, type: "back" },
    { price: null, type: "lay" },
    backDivs[1],
    layDivs[1],
  ];
};

const Inplay = ({ data }) => {
  const navigate = useNavigate();
  const [cricket, setCricket] = useState([]);
  const [avail, setAvail] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isLargeScreen, setIsLargeScreen] = useState(window.innerWidth > 991);
  const { themeColors } = useTheme();
  const [rawMatches, setRawMatches] = useState([]);
  const [marketList, setMarketList] = useState([]);

  const sortMatches = (matches) => {
    return [...matches].sort((a, b) => {

      const aLive = a?.inplay || a?.event?.inPlay || a?.event?.status === "LIVE";
      const bLive = b?.inplay || b?.event?.inPlay || b?.event?.status === "LIVE";

      // 1️⃣ Live matches first
      if (aLive && !bLive) return -1;
      if (!aLive && bLive) return 1;

      // 2️⃣ Then by increasing start time
      const timeA = new Date(a?.event?.openDate).getTime();
      const timeB = new Date(b?.event?.openDate).getTime();

      return timeA - timeB;
    });
  };

  useEffect(() => {
    const handleResize = () => {
      setIsLargeScreen(window.innerWidth > 991);
    };

    window.addEventListener("resize", handleResize);

    // Cleanup on unmount
    return () => window.removeEventListener("resize", handleResize);
  }, []);
  // ✅ STEP 1A: matchData - setRawMatches instead of setCricket
  const matchData = async () => {
    setLoading(true);
    try {
      const response = await axios.get(base_url + "/api/webapi/matchapi", {
        headers: { "Content-Type": "application/json" },
      });
      console.log("API Response:", response?.data);
      const data = response?.data?.result?.data || [];
      const allOpenDates = data.map(dta => dta?.event?.openDate);
      const allData = [];
      data.forEach((dta) => {
        if (compareWithToday(dta?.event?.openDate, allOpenDates)) {
          allData.push(dta);
        }
      });
      console.log("Filtered Matches for Today:", allData);
      setRawMatches(allData); // ✅ changed
    } catch (error) {
      console.error("API Error:", error);
    } finally {
      setLoading(false);
    }
  };

  // ✅ STEP 1B: footballData - setRawMatches instead of setCricket
  const footballData = async () => {
    setLoading(true);
    try {
      const response = await axios.get(base_url + "/api/webapi/footballapi", {
        headers: { "Content-Type": "application/json" },
      });
      console.log("Football API Response:", response?.data);
      const data = response?.data?.result?.data || [];
      const allOpenDates = data.map(dta => dta?.event?.openDate);
      const allData = [];
      data.forEach((dta) => {
        if (compareWithToday(dta?.event?.openDate, allOpenDates)) {
          allData.push(dta);
        }
      });
      console.log("Filtered Football Matches:", allData);
      setRawMatches(allData); // ✅ changed
    } catch (error) {
      console.error("Football API Error:", error);
    } finally {
      setLoading(false);
    }
  };

  // ✅ STEP 1C: tennisData - setRawMatches instead of setCricket
  const tennisData = async () => {
    setLoading(true);
    try {
      const response = await axios.get(base_url + "/api/webapi/tennisapi", {
        headers: { "Content-Type": "application/json" },
      });
      console.log("Tennis API Response:", response?.data);
      const data = response?.data?.result?.data || [];
      const allOpenDates = data.map(dta => dta?.event?.openDate);
      const allData = [];
      data.forEach((dta) => {
        if (compareWithToday(dta?.event?.openDate, allOpenDates)) {
          allData.push(dta);
        }
      });
      console.log("Filtered Tennis Matches:", allData);
      setRawMatches(allData); // ✅ changed
    } catch (error) {
      console.error("Tennis API Error:", error);
    } finally {
      setLoading(false);
    }
  };

  // ✅ STEP 2: rawMatches changes → getMarkets (no more loop because we don't set rawMatches here)
  useEffect(() => {
    const getMarkets = async () => {

      if (rawMatches.length === 0) return;

      try {

        const eventIds = rawMatches.map(item => item?.event?.id);

        const response = await axios.post(
          `${base_url}/game/kheloyaar/getMarkets`,
          { eventIds }
        );

        const markets = response.data.data || [];

        // 🔥 SORT MATCHES
        const sortedMarkets = sortMatches(markets);

        setMarketList(sortedMarkets);
        setCricket(sortedMarkets);

      } catch (err) {
        console.error("Markets Error:", err);
      }

    };

    getMarkets();

  }, [rawMatches]);

  // ✅ STEP 3: marketList changes → fetchOdds (no more loop because we don't set marketList here)
  useEffect(() => {
    const fetchOdds = async () => {
      if (!marketList || marketList.length === 0) return;
      try {
        const marketIds = marketList.map(item => item.marketId).filter(Boolean);
        const chunks = [];
        for (let i = 0; i < marketIds.length; i += 10) {
          chunks.push(marketIds.slice(i, i + 10));
        }
        let allOdds = [];
        for (const chunk of chunks) {
          const response = await axios.post(
            `${base_url}/api/webapi/market/wholeMarket`,
            { marketIds: chunk }
          );
          const oddsData =
            typeof response.data === "string"
              ? JSON.parse(response.data)
              : response.data;
          const markets = oddsData?.data?.data || [];
          allOdds = [...allOdds, ...markets];
        }
        console.log("Fetched Odds Data:", allOdds);
        setAvail(allOdds);
      } catch (error) {
        console.error("Odds Fetch Error:", error);
      }
    };
    fetchOdds();
  }, [marketList]); // ✅ only marketList — no loop;


  const handleClick = (item) => {
    // console.log(item);

    sessionStorage.clear();
    sessionStorage.setItem("marketId", item.marketId);
    sessionStorage.setItem("competetionid", item.competition.id);
    sessionStorage.setItem("eventid", item.event.id);
    sessionStorage.setItem("time", item?.event.openDate);
    sessionStorage.setItem("competitionname", item?.competition.name);
    sessionStorage.setItem("eventname", item?.event.name);

    if (item.eventType.name === "Cricket") {
      navigate("/cricketBet");
      sessionStorage.setItem("selectionid1", item?.runners[0].selectionId);
      sessionStorage.setItem("selectionid2", item?.runners[1].selectionId);

      sessionStorage.setItem("runner1", item.runners[0].runnerName);
      sessionStorage.setItem("runner2", item.runners[1].runnerName);
      // console.log(item.runners, item.runners.length);
      if (item?.runners.length === 3) {
        sessionStorage.setItem("selectionid3", item?.runners[2].selectionId);
        sessionStorage.setItem("runner3", item.runners[2].runnerName);
      }
    } else if (item.eventType.name === "Soccer") {
      navigate("/footBAllBet");
      sessionStorage.setItem("selectionid1", item?.runners[0].selectionId);
      sessionStorage.setItem("selectionid2", item?.runners[1].selectionId);
      sessionStorage.setItem("selectionid3", item?.runners[2].selectionId);

      sessionStorage.setItem("runner1", item.runners[0].runnerName);
      sessionStorage.setItem("runner2", item.runners[1].runnerName);
      sessionStorage.setItem("runner3", item.runners[2].runnerName);
    } else if (item.eventType.name === "Tennis") {
      navigate("/tennis");
      sessionStorage.setItem("selectionid1", item?.runners[0].selectionId);
      sessionStorage.setItem("selectionid2", item?.runners[1].selectionId);

      sessionStorage.setItem("runner1", item.runners[0].runnerName);
      sessionStorage.setItem("runner2", item.runners[1].runnerName);

      if (item?.runners.length === 3) {
        sessionStorage.setItem("selectionid3", item?.runners[2].selectionId);
        sessionStorage.setItem("runner3", item.runners[2].runnerName);
      }
    }
  };

  useEffect(() => {
    const fetchData = async () => {
      if (data === 0) {
        await matchData();
      }
    };

    fetchData();
  }, [data]);

  const filteredCricket = useMemo(() => {
    return cricket.filter((item) => {
      const market = avail.find(
        (m) => m.marketId === item.marketId
      );

      return (
        market &&
        market.runners?.some(
          (runner) =>
            runner?.ex?.availableToBack?.length > 0 ||
            runner?.ex?.availableToLay?.length > 0
        )
      );
    });
  }, [cricket, avail]);



  // console.log('vall',cricket);
  // console.log(avail);
  // console.log(avail);

  return (
    <>
      <div _ngcontent-jrx-c94="" className="wrap-conatiner">
        <div _ngcontent-jrx-c94="">
          <div
            _ngcontent-jrx-c94=""
            className="section tabs_boxes_section no-pad-bot no-pad-top"
          >
            <div _ngcontent-jrx-c94="" className="">
              <div _ngcontent-jrx-c94="" className="sports-wrapper">
                <div className="d-lg-block d-md-block d-none">
                  {loading || (cricket.length > 0 && avail.length === 0) ? (
                    <div
                      className="skeleton-loader"
                      style={{
                        backgroundColor: themeColors["--bg-table"],
                        borderBottom: "1px solid var(--table-border)",
                        padding: "12px",
                      }}
                    >
                      {[...Array(20)].map((_, rowIndex) => (
                        <div
                          key={rowIndex}
                          style={{
                            display: "grid",
                            gridTemplateColumns: "1fr 1fr 1fr 1fr 1fr 1fr",
                            gap: "20px",
                            marginBottom: "18px",
                          }}
                        >
                          {[...Array(6)].map((_, colIndex) => (
                            <div
                              key={colIndex}
                              className="skeleton-bar"
                              style={{
                                height: "20px",
                                borderRadius: "20px",
                              }}
                            />
                          ))}
                        </div>
                      ))}
                    </div>
                  ) : filteredCricket.length > 0 ? (
                    <>
                      <div className="row px-1 py-2 bet-table-header1">
                        <div className="col-lg-8 col-md-8 col-8 mb-0">
                          <h6
                            className="my-0 fw-bold"
                            style={{ color: "#030000" }}
                          >
                            Cricket
                          </h6>
                        </div>
                        <div className="col-lg-4 col-md-4 col-4 mb-0">
                          <div className="row mb-0">
                            <div className="col-lg-4 col-md-4 col-4 mb-0">
                              <h6
                                className="my-0 fw-bold"
                                style={{ color: "#0b0101" }}
                              >
                                1
                              </h6>
                            </div>
                            <div className="col-lg-4 col-md-4 col-4 mb-0">
                              <h6
                                className="my-0 fw-bold"
                                style={{ color: "#0f0000" }}
                              >
                                X
                              </h6>
                            </div>
                            <div className="col-lg-4 col-md-4 col-4 mb-0">
                              <h6
                                className="my-0 fw-bold"
                                style={{ color: "#080000" }}
                              >
                                2
                              </h6>
                            </div>
                          </div>
                        </div>
                      </div>
                      {cricket
                        .filter((item) => {
                          const market = avail.find(m => m.marketId === item.marketId);

                          return (
                            market &&
                            market.runners?.some(
                              runner =>
                                runner?.ex?.availableToBack?.length > 0 ||
                                runner?.ex?.availableToLay?.length > 0
                            )
                          );
                        }).map((item, index) => {
                          const market = avail.find(m => m.marketId === item.marketId);

                          const runner1 = market?.runners?.[0];
                          const runner2 = market?.runners?.[1];

                          const r1Back = runner1?.ex?.availableToBack?.[0]?.price;
                          const r1Lay = runner1?.ex?.availableToLay?.[0]?.price;

                          const r2Back = runner2?.ex?.availableToBack?.[0]?.price;
                          const r2Lay = runner2?.ex?.availableToLay?.[0]?.price;


                          const marketData = market?.runners || [];

                          return (
                            <div
                              key={index}
                              _ngcontent-jrx-c94=""
                              id={item?.competitionId}
                              className="col-12 px-0 ng-star-inserted"
                              style={{ position: "relative" }}
                            >
                              {/* <p>{item.marketId}</p> */}
                              <div
                                _ngcontent-jrx-c94=""
                                className="ng-star-inserted"
                              >
                                <div
                                  _ngcontent-jrx-c94=""
                                  className="section_details_box upcoming-data"
                                >
                                  {item.isStarted && (
                                    <span
                                      _ngcontent-jrx-c94=""
                                      className="innerHTMLScore upcoming-score ng-star-inserted"
                                    >
                                      <div className="cricket-info">
                                        <div className="score">
                                          <span>
                                            item2
                                            <span className="vOver">
                                              (45.0)
                                            </span>
                                          </span>
                                          <span className="vRun">
                                            0 - 0 - 0 - 0 - 1 - 0 -{" "}
                                          </span>
                                          <span>
                                            item
                                            <span className="vOver">(0.0)</span>
                                          </span>
                                        </div>
                                      </div>
                                    </span>
                                  )}

                                  <div
                                    _ngcontent-jrx-c94=""
                                    className="sport-type-detail"
                                  >
                                    <div
                                      _ngcontent-jrx-c94=""
                                      className="runnners-odds-details"
                                    >
                                      <div
                                        _ngcontent-jrx-c94=""
                                        className="sport-name-wrapper"
                                      >
                                        <div
                                          _ngcontent-jrx-c94=""
                                          className="details_box_team"
                                        >
                                          <div
                                            _ngcontent-jrx-c94=""
                                            className="details_box_t_name t_left fs-4 d-flex"
                                            onClick={() => handleClick(item)}
                                          >
                                            {new Date() >=
                                              new Date(item?.event.openDate) ? (
                                              <div
                                                className={
                                                  new Date() >= new Date(item?.event?.openDate)
                                                    ? "green-circle crounded-pill mx-2"
                                                    : ""
                                                }
                                                style={{
                                                  height: "30px",
                                                  width: "70px",
                                                  padding: "6px 0",
                                                  borderRadius: "50rem",
                                                }}
                                              >
                                                <span className="fw-bold mt-1">
                                                  Live
                                                </span>
                                              </div>
                                            ) : (
                                              <span className="event-date-time">
                                                {(() => {
                                                  const date = new Date(item?.event?.openDate);

                                                  const day = String(date.getDate()).padStart(2, "0");

                                                  const month = date.toLocaleString("en-US", {
                                                    month: "short",
                                                  });

                                                  const hours = String(date.getHours()).padStart(2, "0");
                                                  const minutes = String(date.getMinutes()).padStart(2, "0");

                                                  return (
                                                    <>
                                                      <span className="event-date">
                                                        {day} {month}
                                                      </span>

                                                      <span className="event-time">
                                                        {hours}:{minutes}
                                                      </span>
                                                    </>
                                                  );
                                                })()}
                                              </span>
                                            )}{" "}
                                            <span>
                                              <span>
                                                {item.runners[0].runnerName}{" "}
                                              </span>
                                              <span
                                                _ngcontent-jrx-c94=""
                                                className="vs ng-star-inserted"
                                              >
                                                {" "}
                                                vs{" "}
                                              </span>
                                              <span>
                                                {item.runners[1].runnerName}
                                              </span>
                                            </span>
                                          </div>
                                        </div>
                                      </div>
                                      <div
                                        _ngcontent-jrx-c94=""
                                        className="middle-detail"
                                      >
                                        <img
                                          _ngcontent-jrx-c94=""
                                          className="small-grid ng-star-inserted"
                                          style={{
                                            height: "18px",
                                            width: "18px",
                                          }}
                                          src="/assets/paneal_images/tv (1).png"
                                          alt="TVKheloyaar360"
                                        />
                                        <div
                                          _ngcontent-jrx-c94=""
                                          className="small-grid"
                                        >
                                          <span _ngcontent-jrx-c94="">
                                            <img
                                              className="imgfluid mx-2"
                                              src="assets/paneal_images/fancy-icon1.png"
                                            />
                                          </span>
                                          <span _ngcontent-jrx-c94="">
                                            <img
                                              className="imgfluid mx-2"
                                              src="/assets/paneal_images/bm-icon1.png"
                                            />
                                          </span>
                                          <span _ngcontent-jrx-c94="">
                                            <img
                                              className="imgfluid mx-2"
                                              src="/assets/paneal_images/patte.webp"
                                            />
                                          </span>
                                        </div>
                                      </div>
                                      <div
                                        _ngcontent-jrx-c94=""
                                        className="details_odds_boxes_inn details_small_box_wrapper"
                                        style={{ position: "relative" }}
                                      >
                                        <div
                                          _ngcontent-jrx-c94=""
                                          className="details_small_boxes"
                                        >
                                          <div
                                            _ngcontent-jrx-c94=""
                                            className="details_small_boxes_let"
                                          >
                                            <div
                                              _ngcontent-jrx-c94=""
                                              className="details_small_boxes_inn ng-star-inserted"
                                            >
                                              {marketData &&
                                                marketData.length > 0 && (
                                                  <div className="d-flex runnners-odds-details">
                                                    {(() => {
                                                      let backDivs = marketData.map((runner) => ({
                                                        price: runner?.ex?.availableToBack?.[0]?.price || null,
                                                        type: "Back",
                                                      }));

                                                      let layDivs = marketData.map((runner) => ({
                                                        price: runner?.ex?.availableToLay?.[0]?.price || null,
                                                        type: "Lay",
                                                      }));

                                                      while (
                                                        backDivs.length <
                                                        2
                                                      )
                                                        backDivs.push({
                                                          price: null,
                                                          type: "Back",
                                                        });
                                                      while (
                                                        layDivs.length < 2
                                                      )
                                                        layDivs.push({
                                                          price: null,
                                                          type: "Lay",
                                                        });

                                                      let combinedDivs = [
                                                        backDivs[0],
                                                        layDivs[0],
                                                        {
                                                          price: null,
                                                          type: "Back",
                                                        },
                                                        {
                                                          price: null,
                                                          type: "Lay",
                                                        },
                                                        backDivs[1],
                                                        layDivs[1],
                                                      ];

                                                      return combinedDivs.map(
                                                        (item, index) => (
                                                          <app-oddsbutton
                                                            key={index}
                                                          >
                                                            <div
                                                              id={index}
                                                              className={`d-md-flex d-none d-sm-none details_small_blue_box odds-box`}
                                                              style={{
                                                                backgroundColor:
                                                                  item.type ===
                                                                    "Lay"
                                                                    ? themeColors[
                                                                    "--dark-theme-data-box2"
                                                                    ]
                                                                    : themeColors[
                                                                    "--dark-theme-data-box"
                                                                    ],
                                                              }}
                                                            // onClick={() => handleBetClick(item, index, marketid)}
                                                            >
                                                              <span className="ng-star-inserted">
                                                                {item.price !==
                                                                  null
                                                                  ? item.price
                                                                  : "--"}
                                                              </span>
                                                            </div>
                                                          </app-oddsbutton>
                                                        )
                                                      );
                                                    })()}
                                                  </div>
                                                )}
                                            </div>
                                          </div>
                                          <div
                                            _ngcontent-jrx-c94=""
                                            style={{ clear: "both" }}
                                          ></div>
                                        </div>
                                      </div>
                                    </div>
                                  </div>
                                </div>
                              </div>
                            </div>
                          );
                        })}
                    </>
                  ) : (
                    <div
                      className="skeleton-loader"
                      style={{
                        backgroundColor: themeColors["--bg-table"],
                        borderBottom: "1px solid var(--table-border)",
                        padding: "20px",
                      }}
                    >
                      {[...Array(20)].map((_, rowIndex) => (
                        <div
                          key={rowIndex}
                          style={{
                            display: "grid",
                            gridTemplateColumns: "1fr 1fr 1fr 1fr 1fr 1fr",
                            gap: "20px",
                            marginBottom: "18px",
                          }}
                        >
                          {[...Array(6)].map((_, colIndex) => (
                            <div
                              key={colIndex}
                              className="skeleton-bar"
                              style={{
                                height: "12px",
                                borderRadius: "20px",
                              }}
                            />
                          ))}
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                <div className="d-lg-none d-md-none d-block">
                  <div className="row px-1 py-2 bet-table-header1">
                    <div className="col-lg-8 col-md-8 col-8 mb-0">
                      <h6
                        className="my-0 fw-bold"
                        style={{ color: "#fff" }}
                      >
                        Cricket
                      </h6>
                    </div>
                  </div>

                  {loading || (cricket.length > 0 && avail.length === 0) ? (
                    <div
                      className="skeleton-loader"
                      style={{
                        backgroundColor: themeColors["--bg-table"],
                        borderBottom: "1px solid var(--table-border)",
                        padding: "12px",
                      }}
                    >
                      {[...Array(20)].map((_, rowIndex) => (
                        <div
                          key={rowIndex}
                          style={{
                            display: "grid",
                            gridTemplateColumns: "1fr 1fr 1fr 1fr",
                            gap: "20px",
                            marginBottom: "18px",
                          }}
                        >
                          {[...Array(4)].map((_, colIndex) => (
                            <div
                              key={colIndex}
                              className="skeleton-bar"
                              style={{
                                height: "20px",
                                borderRadius: "20px",
                              }}
                            />
                          ))}
                        </div>
                      ))}
                    </div>
                  ) : filteredCricket.length > 0 ? (
                    <>
                      {cricket
                        .filter((item) => {
                          const market = avail.find(m => m.marketId === item.marketId);

                          return (
                            market &&
                            market.runners?.some(
                              runner =>
                                runner?.ex?.availableToBack?.length > 0 ||
                                runner?.ex?.availableToLay?.length > 0
                            )
                          );
                        }).map((item, index) => {
                          const market = avail.find(m => m.marketId === item.marketId);

                          const runner1 = market?.runners?.[0];
                          const runner2 = market?.runners?.[1];

                          const r1Back = runner1?.ex?.availableToBack?.[0]?.price;
                          const r1Lay = runner1?.ex?.availableToLay?.[0]?.price;

                          const r2Back = runner2?.ex?.availableToBack?.[0]?.price;
                          const r2Lay = runner2?.ex?.availableToLay?.[0]?.price;


                          const marketData = market?.runners || [];

                          return (
                            <div
                              key={index}
                              _ngcontent-jrx-c94=""
                              id={item?.competition?.id}
                              className="flex-md-row justify-content-between ng-star-inserted"
                              style={{ position: "relative" }}
                            >
                              <div>
                                {/* <p>{item.marketId}</p> */}
                                <div
                                  _ngcontent-jrx-c94=""
                                  className="ng-star-inserted"
                                >
                                  <div
                                    _ngcontent-jrx-c94=""
                                    className="section_details_box upcoming-data"
                                  >
                                    {item.isStarted && (
                                      <span
                                        _ngcontent-jrx-c94=""
                                        className="innerHTMLScore upcoming-score ng-star-inserted"
                                      >
                                        <div className="cricket-info">
                                          <div className="score">
                                            <span>
                                              item2
                                              <span className="vOver">
                                                (45.0)
                                              </span>
                                            </span>
                                            <span className="vRun">
                                              0 - 0 - 0 - 0 - 1 - 0 -{" "}
                                            </span>
                                            <span>
                                              item
                                              <span className="vOver">
                                                (0.0)
                                              </span>
                                            </span>
                                          </div>
                                        </div>
                                      </span>
                                    )}

                                    <div
                                      _ngcontent-jrx-c94=""
                                      className="sport-type-detail"
                                    >
                                      <div
                                        _ngcontent-jrx-c94=""
                                        className="runnners-odds-details pe-1 d-flex flex-column"
                                      >
                                        <div
                                          _ngcontent-jrx-c94=""
                                          className="sport-name-wrapper"
                                        >
                                          <div
                                            _ngcontent-jrx-c94=""
                                            className="details_box_team py-0 my-0 justify-content-between"
                                          >
                                            <div
                                              _ngcontent-jrx-c94=""
                                              className="details_box_t_name t_left fs-4 d-flex"
                                              onClick={() => handleClick(item)}
                                            >
                                              <div className="data_date my-auto">
                                                {new Date() >=
                                                  new Date(item?.event?.openDate) ? (
                                                  <div
                                                    className={
                                                      new Date() >= new Date(item?.event?.openDate)
                                                        ? "green-circle crounded-pill mx-0"
                                                        : ""
                                                    }
                                                    style={{
                                                      height: "30px",
                                                      width: "70px",
                                                      padding: "6px 0",
                                                      borderRadius: "50rem",
                                                    }}
                                                  >
                                                    <span className="fw-bold mt-1">
                                                      Live
                                                    </span>
                                                  </div>
                                                ) : (
                                                  <span className="event-date-time">
                                                    {(() => {
                                                      const date = new Date(item?.event?.openDate);

                                                      const day = String(date.getDate()).padStart(2, "0");

                                                      const month = date.toLocaleString("en-US", {
                                                        month: "short",
                                                      });

                                                      const hours = String(date.getHours()).padStart(2, "0");
                                                      const minutes = String(date.getMinutes()).padStart(2, "0");

                                                      return (
                                                        <>
                                                          <span className="event-date">
                                                            {day} {month}
                                                          </span>
                                                          <br />
                                                          <span className="event-time">
                                                            {hours}:{minutes}
                                                          </span>
                                                        </>
                                                      );
                                                    })()}
                                                  </span>
                                                )}{" "}
                                              </div>
                                              <div className="my-auto">
                                                {" "}
                                                <span>
                                                  {
                                                    item.runners[0]
                                                      .runnerName
                                                  }{" "}
                                                </span>
                                                <span
                                                  _ngcontent-jrx-c94=""
                                                  className="vs ng-star-inserted"
                                                >
                                                  {" "}
                                                  vs{" "}
                                                </span>
                                                <span>
                                                  {
                                                    item.runners[1]
                                                      .runnerName
                                                  }
                                                </span>
                                              </div>
                                            </div>
                                            <div
                                              _ngcontent-jrx-c94=""
                                              className="middle-detail d-lg-none d-md-none d-block text-nowrap"
                                            >
                                              <div
                                                _ngcontent-jrx-c94=""
                                                className="small-grid"
                                              >
                                                {/* <span
                                                  _ngcontent-jrx-c94=""
                                                  className={`${
                                                    new Date() >=
                                                    new Date(item?.event?.openDate)
                                                      ? "green-circle inplay-blinker ng-star-inserted"
                                                      : ""
                                                  }`}
                                                ></span> */}
                                                <span _ngcontent-jrx-c94="">
                                                  <img
                                                    className="imgfluid ms-1"
                                                    src="assets/paneal_images/fancy-icon1.png"
                                                  />
                                                </span>
                                                <span _ngcontent-jrx-c94="">
                                                  <img
                                                    className="imgfluid"
                                                    src="/assets/paneal_images/bm-icon1.png"
                                                  />
                                                </span>
                                              </div>
                                            </div>
                                          </div>
                                        </div>
                                        {/* <div className="row py-1 bet-table-header2">
                                          <div className="col-md-4 col-4 mb-0">
                                            <h6
                                              className="my-0 fw-bold fs-7 text-center"
                                              style={{
                                                color:
                                                  themeColors["--text-body"],
                                              }}
                                            >
                                              1
                                            </h6>
                                          </div>
                                          <div className="col-md-4 col-4 mb-0">
                                            <h6
                                              className="my-0 fw-bold fs-7 text-center"
                                              style={{
                                                color:
                                                  themeColors["--text-body"],
                                              }}
                                            >
                                              X
                                            </h6>
                                          </div>
                                          <div className="col-md-4 col-4 mb-0">
                                            <h6
                                              className="my-0 fw-bold fs-7 text-center"
                                              style={{
                                                color:
                                                  themeColors["--text-body"],
                                              }}
                                            >
                                              2
                                            </h6>
                                          </div>
                                        </div> */}
                                        <div className="row p-0 me-0">
                                          <div
                                            _ngcontent-jrx-c94=""
                                            className="details_small_boxes pe-0"
                                          >
                                            <div
                                              _ngcontent-jrx-c94=""
                                              className="details_small_boxes_let"
                                            >
                                              <div
                                                _ngcontent-jrx-c94=""
                                                className="details_small_boxes_inn ng-star-inserted w-100 d-block h-100"
                                              >
                                                {marketData &&
                                                  marketData.length > 0 && (
                                                    <div className="d-flex runnners-odds-details h-100 gap-1">
                                                      {(() => {
                                                        let backDivs = marketData.map((runner) => ({
                                                          price: runner?.ex?.availableToBack?.[0]?.price || null,
                                                          type: "Back",
                                                        }));

                                                        let layDivs = marketData.map((runner) => ({
                                                          price: runner?.ex?.availableToLay?.[0]?.price || null,
                                                          type: "Lay",
                                                        }));

                                                        while (
                                                          backDivs.length <
                                                          2
                                                        )
                                                          backDivs.push({
                                                            price: null,
                                                            type: "Back",
                                                          });
                                                        while (
                                                          layDivs.length < 2
                                                        )
                                                          layDivs.push({
                                                            price: null,
                                                            type: "Lay",
                                                          });

                                                        let combinedDivs = [
                                                          backDivs[0],
                                                          layDivs[0],
                                                          {
                                                            price: null,
                                                            type: "Back",
                                                          },
                                                          {
                                                            price: null,
                                                            type: "Lay",
                                                          },
                                                          backDivs[1],
                                                          layDivs[1],
                                                        ];

                                                        return combinedDivs.map(
                                                          (item, index) => (
                                                            <app-oddsbutton
                                                              key={index}
                                                            >
                                                              <div
                                                                id={index}
                                                                className={`d-md-flex d-none d-sm-none details_small_blue_box odds-box`}
                                                                style={{
                                                                  backgroundColor:
                                                                    item.type ===
                                                                      "Lay"
                                                                      ? themeColors[
                                                                      "--dark-theme-data-box2"
                                                                      ]
                                                                      : themeColors[
                                                                      "--dark-theme-data-box"
                                                                      ],
                                                                }}
                                                              // onClick={() => handleBetClick(item, index, marketid)}
                                                              >
                                                                <span className="ng-star-inserted">
                                                                  {item.price !==
                                                                    null
                                                                    ? item.price
                                                                    : "--"}
                                                                </span>
                                                              </div>
                                                            </app-oddsbutton>
                                                          )
                                                        );
                                                      })()}
                                                    </div>
                                                  )}
                                              </div>
                                            </div>
                                            <div
                                              _ngcontent-jrx-c94=""
                                              style={{ clear: "both" }}
                                            ></div>
                                          </div>
                                        </div>
                                      </div>
                                    </div>
                                  </div>
                                </div>
                              </div>



                            </div>
                          );
                        })}
                    </>
                  ) : (
                    <div
                      className="skeleton-loader"
                      style={{
                        backgroundColor: themeColors["--bg-table"],
                        borderBottom: "1px solid var(--table-border)",
                        padding: "20px",
                      }}
                    >
                      {[...Array(20)].map((_, rowIndex) => (
                        <div
                          key={rowIndex}
                          style={{
                            display: "grid",
                            gridTemplateColumns: "1fr 1fr 1fr 1fr",
                            gap: "20px",
                            marginBottom: "18px",
                          }}
                        >
                          {[...Array(4)].map((_, colIndex) => (
                            <div
                              key={colIndex}
                              className="skeleton-bar"
                              style={{
                                height: "12px",
                                borderRadius: "20px",
                              }}
                            />
                          ))}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

const Tomorrow = ({ data }) => {
  const navigate = useNavigate();
  const [cricket, setCricket] = useState([]);
  const [avail, setAvail] = useState();
  const { themeColors } = useTheme();
  let allData = [];

  const isTomorrow = (eventDate) => {
    const now = new Date();
    const tomorrow = new Date();
    tomorrow.setDate(now.getDate() + 1); // Get tomorrow's date

    const tomorrowFormatted = formatDate(tomorrow);
    const eventFormatted = formatDate(eventDate);

    // console.log("Tomorrow's Date:", tomorrowFormatted);
    // console.log("Event Date:", eventFormatted);

    return tomorrowFormatted === eventFormatted; // Compare only dates
  };

  const matchData = async () => {
    try {
      const response = await axios.get(base_url + "/api/webapi/matchapi", {
        headers: {
          "Content-Type": "application/json",
        },
      });
      // console.log("API Response:", response?.data.result);
      const data = response?.data.result.matches;

      data?.map((dta) => {
        const openDate = dta?.openDate;
        // console.log('openDate',openDate);

        // console.log(dta);
        if (isTomorrow(openDate)) {
          allData.push(dta);
        }
      });

      setCricket(allData);
    } catch (error) {
      console.error("API Error:", error);
    }
  };
  const handleClick = (item) => {
    // console.log(item);

    sessionStorage.clear();
    sessionStorage.setItem("marketId", item.marketId);
    sessionStorage.setItem("competetionid", item.competition.id);
    sessionStorage.setItem("eventid", item.event.id);
    sessionStorage.setItem("time", item?.event.openDate);
    sessionStorage.setItem("competitionname", item?.competition.name);
    sessionStorage.setItem("eventname", item?.event.name);

    if (item.eventType.name === "Cricket") {
      navigate("/cricketBet");
      sessionStorage.setItem("selectionid1", item?.runners[0].selectionId);
      sessionStorage.setItem("selectionid2", item?.runners[1].selectionId);

      sessionStorage.setItem("runner1", item.runners[0].runnerName);
      sessionStorage.setItem("runner2", item.runners[1].runnerName);
      // console.log(item.runners, item.runners.length);
      if (item?.runners.length === 3) {
        sessionStorage.setItem("selectionid3", item?.runners[2].selectionId);
        sessionStorage.setItem("runner3", item.runners[2].runnerName);
      }
    } else if (item.eventType.name === "Soccer") {
      navigate("/footBAllBet");
      sessionStorage.setItem("selectionid1", item?.runners[0].selectionId);
      sessionStorage.setItem("selectionid2", item?.runners[1].selectionId);
      sessionStorage.setItem("selectionid3", item?.runners[2].selectionId);

      sessionStorage.setItem("runner1", item.runners[0].runnerName);
      sessionStorage.setItem("runner2", item.runners[1].runnerName);
      sessionStorage.setItem("runner3", item.runners[2].runnerName);
    } else if (item.eventType.name === "Tennis") {
      navigate("/tennis");
      sessionStorage.setItem("selectionid1", item?.runners[0].selectionId);
      sessionStorage.setItem("selectionid2", item?.runners[1].selectionId);

      sessionStorage.setItem("runner1", item.runners[0].runnerName);
      sessionStorage.setItem("runner2", item.runners[1].runnerName);

      if (item?.runners.length === 3) {
        sessionStorage.setItem("selectionid3", item?.runners[2].selectionId);
        sessionStorage.setItem("runner3", item.runners[2].runnerName);
      }
    }
  };

  useEffect(() => {
    const fetchData = async () => {
      if (data === 0) {
        await matchData();
      }
    };

    fetchData();
  }, [data]); // Only when 'data' changes

  // 2. When cricket is updated, then call odds API
  useEffect(() => {
    const oddsData = async () => {
      if (cricket.length === 0) return;

      try {
        const marketid = cricket.map((item) => item.marketId);

        const response = await axios.post(
          `${base_url}/api/webapi/market/wholeMarket`,
          marketid,
          {
            headers: {
              "Content-Type": "application/json",
            },
            timeout: 10000,
          }
        );

        const result = JSON.parse(response.data); // Remove JSON.parse if response is already JSON
        setAvail(result);
      } catch (err) {
        console.error("Error fetching odds data:", err);
      }
    };

    oddsData();
  }, [cricket]);

  return (
    <>
      <div _ngcontent-jrx-c94="" className="wrap-conatiner">
        <div _ngcontent-jrx-c94="">
          <div
            _ngcontent-jrx-c94=""
            className="section tabs_boxes_section no-pad-bot no-pad-top"
          >
            <div _ngcontent-jrx-c94="" className="">
              <div _ngcontent-jrx-c94="">
                {cricket?.length > 0 ? (
                  cricket
                    .filter(
                      (item) =>
                        avail &&
                        avail[item.marketId] &&
                        avail[item.marketId].length > 0 &&
                        avail[item.marketId].some(
                          (market) =>
                            market?.ex?.availableToBack?.length > 0 ||
                            market?.ex?.availableToLay?.length > 0
                        )
                    )
                    .map((item, index) => {
                      const marketData = avail[item.marketId] || [];
                      console.log("arr", marketData);

                      return (
                        <div
                          key={index}
                          _ngcontent-jrx-c94=""
                          id={item?.competition?.id}
                          className="col-12 ng-star-inserted"
                          style={{ position: "relative" }}
                        >
                          <span className="text-yellow fszz">
                            {new Date(item?.event.openDate).toLocaleTimeString(
                              [],
                              {
                                hour: "2-digit",
                                minute: "2-digit",
                                hour12: false,
                              }
                            )}
                          </span>{" "}
                          <span className="text-yellow fszz">
                            {/*new Date(item?.event.openDate).toISOString().split("T")[0]*/}
                            Tomorrow
                          </span>
                          {/* <p>{item.marketId}</p> */}
                          <div
                            _ngcontent-jrx-c94=""
                            className="ng-star-inserted"
                          >
                            <div
                              _ngcontent-jrx-c94=""
                              className="section_details_box upcoming-data"
                            >
                              {item.isStarted && (
                                <span
                                  _ngcontent-jrx-c94=""
                                  className="innerHTMLScore upcoming-score ng-star-inserted"
                                >
                                  <div className="cricket-info">
                                    <div className="score">
                                      <span>
                                        {item.title1}
                                        <span className="vOver">(45.0)</span>
                                      </span>
                                      <span className="vRun">
                                        0 - 0 - 0 - 0 - 1 - 0 -{" "}
                                      </span>
                                      <span>
                                        {item.title2}
                                        <span className="vOver">(0.0)</span>
                                      </span>
                                    </div>
                                  </div>
                                </span>
                              )}

                              <div
                                _ngcontent-jrx-c94=""
                                className="sport-type-detail"
                              >
                                <div
                                  _ngcontent-jrx-c94=""
                                  className="runnners-odds-details"
                                >
                                  <div
                                    _ngcontent-jrx-c94=""
                                    className="sport-name-wrapper"
                                  >
                                    <div
                                      _ngcontent-jrx-c94=""
                                      className="details_box_team"
                                    >
                                      <div
                                        _ngcontent-jrx-c94=""
                                        className="details_box_t_name t_left fs-4"
                                        onClick={() => handleClick(item)}
                                      >
                                        {" "}
                                        {item.runners[0].runnerName}{" "}
                                        <span
                                          _ngcontent-jrx-c94=""
                                          className="vs ng-star-inserted"
                                        >
                                          {" "}
                                          vs{" "}
                                        </span>
                                        {item.runners[1].runnerName}
                                      </div>
                                    </div>
                                  </div>
                                  <div
                                    _ngcontent-jrx-c94=""
                                    className="middle-detail"
                                  >
                                    <div
                                      _ngcontent-jrx-c94=""
                                      className={`${item.isStarted
                                        ? "green-circle inplay-blinker ng-star-inserted"
                                        : ""
                                        }`}
                                    ></div>
                                    <img
                                      _ngcontent-jrx-c94=""
                                      className="small-grid ng-star-inserted"
                                      style={{ height: "18px", width: "18px" }}
                                      src="/assets/paneal_images/tv (1).png"
                                      alt="TVKheloyaar360"
                                    />

                                    <div
                                      _ngcontent-jrx-c94=""
                                      className="small-grid"
                                    >
                                      <span _ngcontent-jrx-c94="">
                                        <img
                                          className="imgfluid mx-2"
                                          src="assets/paneal_images/fancy-icon1.png"
                                        />
                                      </span>
                                      <span _ngcontent-jrx-c94="">
                                        <img
                                          className="imgfluid mx-2"
                                          src="/assets/paneal_images/bm-icon1.png"
                                        />
                                      </span>
                                      <span _ngcontent-jrx-c94="">
                                        <img
                                          className="imgfluid mx-2"
                                          src="/assets/paneal_images/patte.webp"
                                        />
                                      </span>
                                    </div>
                                    {/* <div
                                      _ngcontent-jrx-c94=""
                                      className="small-grid d-md-block d-none"
                                    >
                                      <span _ngcontent-jrx-c94=""> BF</span>
                                    </div> */}
                                  </div>
                                  <div
                                    _ngcontent-jrx-c94=""
                                    className="details_odds_boxes_inn details_small_box_wrapper"
                                    style={{ position: "relative" }}
                                  >
                                    <div
                                      _ngcontent-jrx-c94=""
                                      className="details_small_boxes"
                                    >
                                      <div
                                        _ngcontent-jrx-c94=""
                                        className="details_small_boxes_let"
                                      >
                                        <div
                                          _ngcontent-jrx-c94=""
                                          className="details_small_boxes_inn ng-star-inserted"
                                        >
                                          {marketData &&
                                            marketData.length > 0 && (
                                              <div className="d-flex runnners-odds-details">
                                                {(() => {
                                                  let backDivs =
                                                    marketData.flatMap(
                                                      (market) =>
                                                        market?.ex
                                                          ?.availableToBack
                                                          ?.length > 0
                                                          ? [
                                                            {
                                                              price:
                                                                market.ex
                                                                  .availableToBack[0]
                                                                  .price,
                                                              type: "back",
                                                            },
                                                          ]
                                                          : []
                                                    );
                                                  let layDivs =
                                                    marketData.flatMap(
                                                      (market) =>
                                                        market?.ex
                                                          ?.availableToLay
                                                          ?.length > 0
                                                          ? [
                                                            {
                                                              price:
                                                                market.ex
                                                                  .availableToLay[0]
                                                                  .price,
                                                              type: "lay",
                                                            },
                                                          ]
                                                          : []
                                                    );

                                                  // Fill up with empty divs if needed to ensure 6 elements
                                                  while (backDivs.length < 2)
                                                    backDivs.push({
                                                      price: null,
                                                      type: "back",
                                                    });
                                                  while (layDivs.length < 2)
                                                    layDivs.push({
                                                      price: null,
                                                      type: "lay",
                                                    });

                                                  let combinedDivs = [
                                                    backDivs[0],
                                                    layDivs[0],
                                                    {
                                                      price: null,
                                                      type: "back",
                                                    }, // Empty Back
                                                    {
                                                      price: null,
                                                      type: "lay",
                                                    }, // Empty Lay
                                                    backDivs[1],
                                                    layDivs[1],
                                                  ];

                                                  return combinedDivs.map(
                                                    (item, index) => (
                                                      <app-oddsbutton
                                                        key={index}
                                                      >
                                                        <div
                                                          id={index}
                                                          className={`d-md-flex ${item.type === "lay"
                                                            ? "bg-pink"
                                                            : ""
                                                            } d-none d-sm-none details_small_blue_box odds-box `}
                                                        >
                                                          <span className="ng-star-inserted">
                                                            {item.price !== null
                                                              ? item.price
                                                              : "--"}
                                                          </span>
                                                        </div>
                                                      </app-oddsbutton>
                                                    )
                                                  );
                                                })()}
                                              </div>
                                            )}
                                        </div>
                                      </div>
                                      <div
                                        _ngcontent-jrx-c94=""
                                        style={{ clear: "both" }}
                                      ></div>
                                    </div>
                                  </div>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      );
                    })
                ) : (
                  <div>No Matches Found.</div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

const Upcoming = ({ data }) => {
  const navigate = useNavigate();

  const [cricket, setCricket] = useState([]);
  const [avail, setAvail] = useState([]);
  const [isLargeScreen, setIsLargeScreen] = useState(window.innerWidth > 991);
  const { themeColors } = useTheme();

  useEffect(() => {
    const handleResize = () => {
      setIsLargeScreen(window.innerWidth > 991);
    };

    window.addEventListener("resize", handleResize);

    // Cleanup on unmount
    return () => window.removeEventListener("resize", handleResize);
  }, []);
  let allData = [];

  const isAfterTomorrow = (eventDate) => {
    const now = new Date();
    const afterTomorrow = new Date();
    afterTomorrow.setDate(now.getDate() + 2); // Get the day after tomorrow

    const eventFormatted = formatDate(eventDate);
    const afterTomorrowFormatted = formatDate(afterTomorrow);
    // console.log('afterr',afterTomorrowFormatted);

    return eventFormatted >= afterTomorrowFormatted; // Checks if event is after tomorrow
  };

  const matchData = async () => {
    try {
      const response = await axios.get(base_url + "/api/webapi/matchapi", {
        headers: {
          "Content-Type": "application/json",
        },
      });
      // console.log("API Response:", response?.data.result);
      const data = response?.data.result;

      data?.map((dta) => {
        const openDate = dta?.event.openDate;
        // console.log('openDate',openDate);

        // console.log(dta);
        if (isAfterTomorrow(openDate)) {
          allData.push(dta);
        }
      });

      // console.log('add',allData);
      setCricket(allData);
    } catch (error) {
      console.error("API Error:", error);
    }
  };

  useEffect(() => {
    const fetchData = async () => {
      if (data === 0) {
        await matchData();
      }
    };

    fetchData();
  }, [data]); // Only when 'data' changes

  // 2. When cricket is updated, then call odds API
  useEffect(() => {
    const oddsData = async () => {
      if (cricket.length === 0) return;

      try {
        const marketid = cricket.map((item) => item.marketId);

        const response = await axios.post(
          `${base_url}/api/webapi/market/wholeMarket`,
          marketid,
          {
            headers: {
              "Content-Type": "application/json",
            },
            timeout: 10000,
          }
        );

        const result = JSON.parse(response.data); // Remove JSON.parse if response is already JSON
        setAvail(result);
      } catch (err) {
        console.error("Error fetching odds data:", err);
      }
    };

    oddsData();
  }, [cricket]);


  const handleClick = (item) => {
    // console.log(item);

    sessionStorage.clear();
    sessionStorage.setItem("marketId", item.marketId);
    sessionStorage.setItem("competetionid", item.competition.id);
    sessionStorage.setItem("eventid", item.event.id);
    sessionStorage.setItem("time", item?.event.openDate);
    sessionStorage.setItem("competitionname", item?.competition.name);
    sessionStorage.setItem("eventname", item?.event.name);

    if (item.eventType.name === "Cricket") {
      navigate("/cricketBet");
      sessionStorage.setItem("selectionid1", item?.runners[0].selectionId);
      sessionStorage.setItem("selectionid2", item?.runners[1].selectionId);

      sessionStorage.setItem("runner1", item.runners[0].runnerName);
      sessionStorage.setItem("runner2", item.runners[1].runnerName);
      // console.log(item.runners, item.runners.length);
      if (item?.runners.length === 3) {
        sessionStorage.setItem("selectionid3", item?.runners[2].selectionId);
        sessionStorage.setItem("runner3", item.runners[2].runnerName);
      }
    } else if (item.eventType.name === "Soccer") {
      navigate("/footBAllBet");
      sessionStorage.setItem("selectionid1", item?.runners[0].selectionId);
      sessionStorage.setItem("selectionid2", item?.runners[1].selectionId);
      sessionStorage.setItem("selectionid3", item?.runners[2].selectionId);

      sessionStorage.setItem("runner1", item.runners[0].runnerName);
      sessionStorage.setItem("runner2", item.runners[1].runnerName);
      sessionStorage.setItem("runner3", item.runners[2].runnerName);
    } else if (item.eventType.name === "Tennis") {
      navigate("/tennis");
      sessionStorage.setItem("selectionid1", item?.runners[0].selectionId);
      sessionStorage.setItem("selectionid2", item?.runners[1].selectionId);

      sessionStorage.setItem("runner1", item.runners[0].runnerName);
      sessionStorage.setItem("runner2", item.runners[1].runnerName);

      if (item?.runners.length === 3) {
        sessionStorage.setItem("selectionid3", item?.runners[2].selectionId);
        sessionStorage.setItem("runner3", item.runners[2].runnerName);
      }
    }
  };

  return (
    <>
      <div _ngcontent-jrx-c94="" className="wrap-conatiner">
        <div _ngcontent-jrx-c94="">
          <div
            _ngcontent-jrx-c94=""
            className="section tabs_boxes_section no-pad-bot no-pad-top"
          >
            <div _ngcontent-jrx-c94="" className="">
              <div _ngcontent-jrx-c94="" className="sports-wrapper">
                {isLargeScreen &&
                  (cricket?.length > 0 ? (
                    <>
                      <div className="row px-1 py-2 bet-table-header1">
                        <div className="col-lg-8 mb-0">
                          <h6
                            className="my-0 fw-bold"
                            style={{ color: "#fff" }}
                          >
                            Cricket
                          </h6>
                        </div>
                        <div className="col-lg-4 mb-0">
                          <div className="row mb-0">
                            <div className="col-lg-4 mb-0">
                              <h6
                                className="my-0 fw-bold"
                                style={{ color: "#fff" }}
                              >
                                1
                              </h6>
                            </div>
                            <div className="col-lg-4 mb-0">
                              <h6
                                className="my-0 fw-bold"
                                style={{ color: "#fff" }}
                              >
                                X
                              </h6>
                            </div>
                            <div className="col-lg-4 mb-0">
                              <h6
                                className="my-0 fw-bold"
                                style={{ color: "#fff" }}
                              >
                                2
                              </h6>
                            </div>
                          </div>
                        </div>
                      </div>
                      {cricket
                        .filter(
                          (item) =>
                            avail &&
                            avail[item.marketId] &&
                            avail[item.marketId].length > 0 &&
                            avail[item.marketId].some(
                              (market) =>
                                market?.ex?.availableToBack?.length > 0 ||
                                market?.ex?.availableToLay?.length > 0
                            )
                        )
                        .map((item, index) => {
                          const marketData = avail[item.marketId] || [];
                          // console.log('arr',marketData);

                          return (
                            <div
                              key={index}
                              _ngcontent-jrx-c94=""
                              id={item?.competition?.id}
                              className="col-12 px-0 ng-star-inserted"
                              style={{ position: "relative" }}
                            >
                              <span className="text-yellow fszz">
                                {new Date(
                                  item?.event.openDate
                                ).toLocaleTimeString([], {
                                  hour: "2-digit",
                                  minute: "2-digit",
                                  hour12: false,
                                })}
                              </span>{" "}
                              <span className="text-yellow fszz">
                                {
                                  new Date(item?.event.openDate)
                                    .toISOString()
                                    .split("T")[0]
                                }
                              </span>
                              {/* <p>{item.marketId}</p> */}
                              <div
                                _ngcontent-jrx-c94=""
                                className="ng-star-inserted"
                              >
                                <div
                                  _ngcontent-jrx-c94=""
                                  className="section_details_box upcoming-data"
                                >
                                  {item.isStarted && (
                                    <span
                                      _ngcontent-jrx-c94=""
                                      className="innerHTMLScore upcoming-score ng-star-inserted"
                                    >
                                      <div className="cricket-info">
                                        <div className="score">
                                          <span>
                                            {item.title1}
                                            <span className="vOver">
                                              (45.0)
                                            </span>
                                          </span>
                                          <span className="vRun">
                                            0 - 0 - 0 - 0 - 1 - 0 -{" "}
                                          </span>
                                          <span>
                                            {item.title2}
                                            <span className="vOver">(0.0)</span>
                                          </span>
                                        </div>
                                      </div>
                                    </span>
                                  )}

                                  <div
                                    _ngcontent-jrx-c94=""
                                    className="sport-type-detail"
                                  >
                                    <div
                                      _ngcontent-jrx-c94=""
                                      className="runnners-odds-details"
                                    >
                                      <div
                                        _ngcontent-jrx-c94=""
                                        className="sport-name-wrapper"
                                      >
                                        <div
                                          _ngcontent-jrx-c94=""
                                          className="details_box_team"
                                        >
                                          <div
                                            _ngcontent-jrx-c94=""
                                            className="details_box_t_name t_left fs-4"
                                            onClick={() => handleClick(item)}
                                          >
                                            {" "}
                                            {item?.event?.name}{" "}
                                          </div>
                                        </div>
                                      </div>
                                      <div
                                        _ngcontent-jrx-c94=""
                                        className="middle-detail"
                                      >
                                        <div
                                          _ngcontent-jrx-c94=""
                                          className={`${item.isStarted
                                            ? "green-circle inplay-blinker ng-star-inserted"
                                            : ""
                                            }`}
                                        ></div>
                                        <img
                                          _ngcontent-jrx-c94=""
                                          className="small-grid ng-star-inserted"
                                          style={{
                                            height: "18px",
                                            width: "18px",
                                          }}
                                          src="/assets/paneal_images/tv (1).png"
                                          alt="TVKheloyaar360"
                                        />

                                        <div
                                          _ngcontent-jrx-c94=""
                                          className="small-grid"
                                        >
                                          <span _ngcontent-jrx-c94="">
                                            <img
                                              className="imgfluid mx-2"
                                              src="assets/paneal_images/fancy-icon1.png"
                                            />
                                          </span>
                                          <span _ngcontent-jrx-c94="">
                                            <img
                                              className="imgfluid mx-2"
                                              src="/assets/paneal_images/bm-icon1.png"
                                            />
                                          </span>
                                          <span _ngcontent-jrx-c94="">
                                            <img
                                              className="imgfluid mx-2"
                                              src="/assets/paneal_images/patte.webp"
                                            />
                                          </span>
                                        </div>
                                        {/* <div
                                      _ngcontent-jrx-c94=""
                                      className="small-grid d-md-block d-none"
                                    >
                                      <span _ngcontent-jrx-c94=""> BF</span>
                                    </div> */}
                                      </div>
                                      <div
                                        _ngcontent-jrx-c94=""
                                        className="details_odds_boxes_inn details_small_box_wrapper"
                                        style={{ position: "relative" }}
                                      >
                                        <div
                                          _ngcontent-jrx-c94=""
                                          className="details_small_boxes"
                                        >
                                          <div
                                            _ngcontent-jrx-c94=""
                                            className="details_small_boxes_let"
                                          >
                                            <div
                                              _ngcontent-jrx-c94=""
                                              className="details_small_boxes_inn ng-star-inserted"
                                            >
                                              {marketData &&
                                                marketData.length > 0 && (
                                                  <div className="d-flex runnners-odds-details">
                                                    {(() => {
                                                      let backDivs =
                                                        marketData.flatMap(
                                                          (market) =>
                                                            market?.ex
                                                              ?.availableToBack
                                                              ?.length > 0
                                                              ? [
                                                                {
                                                                  price:
                                                                    market.ex
                                                                      .availableToBack[0]
                                                                      .price,
                                                                  type: "back",
                                                                },
                                                              ]
                                                              : []
                                                        );
                                                      let layDivs =
                                                        marketData.flatMap(
                                                          (market) =>
                                                            market?.ex
                                                              ?.availableToLay
                                                              ?.length > 0
                                                              ? [
                                                                {
                                                                  price:
                                                                    market.ex
                                                                      .availableToLay[0]
                                                                      .price,
                                                                  type: "lay",
                                                                },
                                                              ]
                                                              : []
                                                        );

                                                      // Fill up with empty divs if needed to ensure 6 elements
                                                      while (
                                                        backDivs.length < 2
                                                      )
                                                        backDivs.push({
                                                          price: null,
                                                          type: "back",
                                                        });
                                                      while (layDivs.length < 2)
                                                        layDivs.push({
                                                          price: null,
                                                          type: "lay",
                                                        });

                                                      let combinedDivs = [
                                                        backDivs[0],
                                                        layDivs[0],
                                                        {
                                                          price: null,
                                                          type: "back",
                                                        }, // Empty Back
                                                        {
                                                          price: null,
                                                          type: "lay",
                                                        }, // Empty Lay
                                                        backDivs[1],
                                                        layDivs[1],
                                                      ];

                                                      return combinedDivs.map(
                                                        (item, index) => (
                                                          <app-oddsbutton
                                                            key={index}
                                                          >
                                                            <div
                                                              id={index}
                                                              className={`d-md-flex ${item.type ===
                                                                "lay"
                                                                ? "bg-pink"
                                                                : ""
                                                                } d-none d-sm-none details_small_blue_box odds-box `}
                                                            >
                                                              <span className="ng-star-inserted">
                                                                {item.price !==
                                                                  null
                                                                  ? item.price
                                                                  : "--"}
                                                              </span>
                                                            </div>
                                                          </app-oddsbutton>
                                                        )
                                                      );
                                                    })()}
                                                  </div>
                                                )}
                                            </div>
                                          </div>
                                          <div
                                            _ngcontent-jrx-c94=""
                                            style={{ clear: "both" }}
                                          ></div>
                                        </div>
                                      </div>
                                    </div>
                                  </div>
                                </div>
                              </div>
                            </div>
                          );
                        })}
                    </>
                  ) : (
                    <div>No Matches Found.</div>
                  ))}

                {!isLargeScreen &&
                  (cricket?.length > 0 ? (
                    cricket
                      .filter(
                        (item) =>
                          avail &&
                          avail[item.marketId] &&
                          avail[item.marketId].length > 0 &&
                          avail[item.marketId].some(
                            (market) =>
                              market?.ex?.availableToBack?.length > 0 ||
                              market?.ex?.availableToLay?.length > 0
                          )
                      )
                      .map((item, index) => {
                        const marketData = avail[item.marketId] || [];
                        // console.log('arr',marketData);

                        return (
                          <div
                            key={index}
                            _ngcontent-jrx-c94=""
                            id={item?.competition?.id}
                            className="col-12 px-0 ng-star-inserted"
                            style={{ position: "relative" }}
                          >
                            <span className="text-yellow fszz">
                              {new Date(
                                item?.event.openDate
                              ).toLocaleTimeString([], {
                                hour: "2-digit",
                                minute: "2-digit",
                                hour12: false,
                              })}
                            </span>{" "}
                            <span className="text-yellow fszz">
                              {
                                new Date(item?.event.openDate)
                                  .toISOString()
                                  .split("T")[0]
                              }
                            </span>
                            {/* <p>{item.marketId}</p> */}
                            <div
                              _ngcontent-jrx-c94=""
                              className="ng-star-inserted"
                            >
                              <div
                                _ngcontent-jrx-c94=""
                                className="section_details_box upcoming-data"
                              >
                                {item.isStarted && (
                                  <span
                                    _ngcontent-jrx-c94=""
                                    className="innerHTMLScore upcoming-score ng-star-inserted"
                                  >
                                    <div className="cricket-info">
                                      <div className="score">
                                        <span>
                                          {item.title1}
                                          <span className="vOver">(45.0)</span>
                                        </span>
                                        <span className="vRun">
                                          0 - 0 - 0 - 0 - 1 - 0 -{" "}
                                        </span>
                                        <span>
                                          {item.title2}
                                          <span className="vOver">(0.0)</span>
                                        </span>
                                      </div>
                                    </div>
                                  </span>
                                )}

                                <div
                                  _ngcontent-jrx-c94=""
                                  className="sport-type-detail"
                                >
                                  <div
                                    _ngcontent-jrx-c94=""
                                    className="runnners-odds-details"
                                  >
                                    <div
                                      _ngcontent-jrx-c94=""
                                      className="sport-name-wrapper"
                                    >
                                      <div
                                        _ngcontent-jrx-c94=""
                                        className="details_box_team"
                                      >
                                        <div
                                          _ngcontent-jrx-c94=""
                                          className="details_box_t_name t_left fs-4"
                                          onClick={() => handleClick(item)}
                                        >
                                          {" "}
                                          {item?.event?.name}{" "}
                                        </div>
                                      </div>
                                    </div>
                                    <div
                                      _ngcontent-jrx-c94=""
                                      className="middle-detail"
                                    >
                                      <div
                                        _ngcontent-jrx-c94=""
                                        className={`${item.isStarted
                                          ? "green-circle inplay-blinker ng-star-inserted"
                                          : ""
                                          }`}
                                      ></div>
                                      <img
                                        _ngcontent-jrx-c94=""
                                        className="small-grid ng-star-inserted"
                                        style={{
                                          height: "18px",
                                          width: "18px",
                                        }}
                                        src="/assets/paneal_images/tv (1).png"
                                        alt="TVKheloyaar360"
                                      />

                                      <div
                                        _ngcontent-jrx-c94=""
                                        className="small-grid"
                                      >
                                        <span _ngcontent-jrx-c94="">
                                          <img
                                            className="imgfluid mx-2"
                                            src="assets/paneal_images/fancy-icon1.png"
                                          />
                                        </span>
                                        <span _ngcontent-jrx-c94="">
                                          <img
                                            className="imgfluid mx-2"
                                            src="/assets/paneal_images/bm-icon1.png"
                                          />
                                        </span>
                                        <span _ngcontent-jrx-c94="">
                                          <img
                                            className="imgfluid mx-2"
                                            src="/assets/paneal_images/patte.webp"
                                          />
                                        </span>
                                      </div>
                                      {/* <div
                                      _ngcontent-jrx-c94=""
                                      className="small-grid d-md-block d-none"
                                    >
                                      <span _ngcontent-jrx-c94=""> BF</span>
                                    </div> */}
                                    </div>
                                    <div
                                      _ngcontent-jrx-c94=""
                                      className="details_odds_boxes_inn details_small_box_wrapper"
                                      style={{ position: "relative" }}
                                    >
                                      <div
                                        _ngcontent-jrx-c94=""
                                        className="details_small_boxes"
                                      >
                                        <div
                                          _ngcontent-jrx-c94=""
                                          className="details_small_boxes_let"
                                        >
                                          <div
                                            _ngcontent-jrx-c94=""
                                            className="details_small_boxes_inn ng-star-inserted"
                                          >
                                            {marketData &&
                                              marketData.length > 0 && (
                                                <div className="d-flex runnners-odds-details">
                                                  {(() => {
                                                    let backDivs =
                                                      marketData.flatMap(
                                                        (market) =>
                                                          market?.ex
                                                            ?.availableToBack
                                                            ?.length > 0
                                                            ? [
                                                              {
                                                                price:
                                                                  market.ex
                                                                    .availableToBack[0]
                                                                    .price,
                                                                type: "back",
                                                              },
                                                            ]
                                                            : []
                                                      );
                                                    let layDivs =
                                                      marketData.flatMap(
                                                        (market) =>
                                                          market?.ex
                                                            ?.availableToLay
                                                            ?.length > 0
                                                            ? [
                                                              {
                                                                price:
                                                                  market.ex
                                                                    .availableToLay[0]
                                                                    .price,
                                                                type: "lay",
                                                              },
                                                            ]
                                                            : []
                                                      );

                                                    // Fill up with empty divs if needed to ensure 6 elements
                                                    while (backDivs.length < 2)
                                                      backDivs.push({
                                                        price: null,
                                                        type: "back",
                                                      });
                                                    while (layDivs.length < 2)
                                                      layDivs.push({
                                                        price: null,
                                                        type: "lay",
                                                      });

                                                    let combinedDivs = [
                                                      backDivs[0],
                                                      layDivs[0],
                                                      {
                                                        price: null,
                                                        type: "back",
                                                      }, // Empty Back
                                                      {
                                                        price: null,
                                                        type: "lay",
                                                      }, // Empty Lay
                                                      backDivs[1],
                                                      layDivs[1],
                                                    ];

                                                    return combinedDivs.map(
                                                      (item, index) => (
                                                        <app-oddsbutton
                                                          key={index}
                                                        >
                                                          <div
                                                            id={index}
                                                            className={`d-md-flex ${item.type ===
                                                              "lay"
                                                              ? "bg-pink"
                                                              : ""
                                                              } d-none d-sm-none details_small_blue_box odds-box `}
                                                          >
                                                            <span className="ng-star-inserted">
                                                              {item.price !==
                                                                null
                                                                ? item.price
                                                                : "--"}
                                                            </span>
                                                          </div>
                                                        </app-oddsbutton>
                                                      )
                                                    );
                                                  })()}
                                                </div>
                                              )}
                                          </div>
                                        </div>
                                        <div
                                          _ngcontent-jrx-c94=""
                                          style={{ clear: "both" }}
                                        ></div>
                                      </div>
                                    </div>
                                  </div>
                                </div>
                              </div>
                            </div>
                          </div>
                        );
                      })
                  ) : (
                    <div>No Matches Found.</div>
                  ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};
const CricketReal = () => {
  const [isSidebar, setSidebar] = useState(false);
  const [isInplay, setInplay] = useState(true);
  const [isTomorrow, setTomorrow] = useState(false);
  const [isUpcoming, setUpcoming] = useState(false);
  const [isActive, setActiveLink] = useState(0);
  const { themeColors } = useTheme();

  const [showCanvas, setShowCanvas] = useState(false);

  const handleCanvasShow = () => setShowCanvas(true);
  const handleCanvasClose = () => setShowCanvas(false);

  const handlePlay = (id) => {
    setInplay(false);
    setTomorrow(false);
    setUpcoming(false);

    if (id === "inplay") {
      setInplay(true);
    } else if (id === "tomorrow") {
      setTomorrow(true);
    } else {
      setUpcoming(true);
    }
  };

  useEffect(() => {
    window.scrollTo(0, 0);
    const handleResize = () => {
      if (window.innerWidth > 991) {
        setSidebar(true);
      } else {
        setSidebar(false);
      }
    };
    handleResize();
  }, []);

  const [isLargeScreen, setIsLargeScreen] = useState(window.innerWidth > 991);

  useEffect(() => {
    const handleResize = () => {
      setIsLargeScreen(window.innerWidth > 991);
    };

    window.addEventListener("resize", handleResize);

    return () => window.removeEventListener("resize", handleResize);
  }, []);

  return (
    <app-root _nghost-jrx-c101="" ng-version="12.2.16">
      <div _ngcontent-jrx-c101="">
        <app-toasts
          _ngcontent-jrx-c101=""
          aria-live="polite"
          aria-atomic="true"
          className="ngb-toasts"
        ></app-toasts>
        <router-outlet _ngcontent-jrx-c101=""></router-outlet>
        <app-landing-page-new _nghost-jrx-c95="" className="ng-star-inserted">
          <div _ngcontent-jrx-c95="" className="head-container">
            <Header
              isSidebar={isSidebar}
              setSidebar={setSidebar}
              show={showCanvas}
              handleShow={handleCanvasShow}
              handleClose={handleCanvasClose}
            />

            <div _ngcontent-jrx-c95="" className="body-container mb-1">
              <Sidebar isSidebar={isSidebar} setSidebar={setSidebar} />
              <div
                _ngcontent-jrx-c95=""
                className={`rightbar`}
                style={{
                  paddingLeft: isLargeScreen ? "0px" : "0px",
                  width: isSidebar ? "" : "1100%",
                  transition: "width 0.3s ease-in-out",
                  backgroundColor: themeColors["--dark-theme-data-bg"],
                }}
              >
                <app-upcoming
                  _ngcontent-jrx-c95=""
                  _nghost-jrx-c94=""
                  className="ng-star-inserted"
                  style={{ backgroundColor: "white !important" }}
                >
                  {isInplay && <Inplay data={isActive} />}
                  {isTomorrow && <Tomorrow data={isActive} />}
                  {isUpcoming && <Upcoming data={isActive} />}
                </app-upcoming>

                {/* <Footer /> */}
              </div>
            </div>
            <MarqueeBottom onMenuClick={handleCanvasShow} />
          </div>
        </app-landing-page-new>

        <div className="slider-container"></div>
      </div>
    </app-root>
  );
};

export default CricketReal;
